import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";
import {
  AddDentistSessionModal,
  type DentistSessionFormRow,
} from "../components/AddDentistSessionModal";

type DentistRow = DentistSessionFormRow;

function ensureDrPrefix(name: string): string {
  const n = String(name ?? "").trim();
  if (!n) return "";
  if (/^dr\.?\s+/i.test(n)) return n.replace(/^dr\.?\s+/i, "Dr. ");
  return `Dr. ${n}`;
}

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const initialRows: DentistRow[] = [];

export default function DentistGeneralHoursTab() {
  const [rows, setRows] = useState<DentistRow[]>(initialRows);
  const [filterDentist, setFilterDentist] = useState<string>("all");
  const [filterDay, setFilterDay] = useState<string>("all");
  const [tableLoading, setTableLoading] = useState(false);
  const [deleteLoadingKey, setDeleteLoadingKey] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editSource, setEditSource] = useState<DentistRow | null>(null);

  const dentistNameOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      const n = String(r.dentistName ?? "").trim();
      if (n) s.add(n);
    });
    return Array.from(s);
  }, [rows]);

  const refreshTable = async () => {
    setTableLoading(true);
    try {
      const res = await baseUrl.get("/client/setting/dentistGeneralHours/list");
      const raw = res.data?.data?.sessions;
      const list: DentistRow[] = Array.isArray(raw)
        ? raw
            .map((x: unknown) => {
              const o = x as Record<string, unknown>;
              return {
                dentistName: ensureDrPrefix(String(o?.dentistName ?? "")),
                day: String(o?.day ?? ""),
                sessionName: String(o?.sessionName ?? ""),
                startTime: String(o?.startTime ?? ""),
                endTime: String(o?.endTime ?? ""),
                roomNo: String(o?.roomNo ?? ""),
                doctorId: String(o?.doctorId ?? ""),
                roomId: String(o?.roomId ?? ""),
              };
            })
            .filter((r: DentistRow) => r.dentistName && r.sessionName && r.day)
        : [];
      setRows(list);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Failed to load dentist general hours"
      );
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    refreshTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDeleteRow = async (r: DentistRow) => {
    const doctorId = String(r.doctorId ?? "").trim();
    const roomId = String(r.roomId ?? "").trim();
    const day = String(r.day ?? "").trim();
    const sessionName = String(r.sessionName ?? "").trim();
    if (!doctorId || !day || !sessionName) {
      toast.error("Missing row identifiers to delete.");
      return;
    }

    const key = `${doctorId}__${roomId || "unassigned"}__${day}__${sessionName}`;
    const roomLabel = roomId ? `in room ${r.roomNo}` : "without a room";
    if (
      !window.confirm(
        `Delete ${r.dentistName} — ${sessionName} (${day}) ${roomLabel}?`
      )
    ) {
      return;
    }

    setDeleteLoadingKey(key);
    try {
      await baseUrl.post("/client/setting/dentistGeneralHours/deleteSession", {
        doctorId,
        ...(roomId ? { roomId } : {}),
        day,
        sessionName,
      });
      toast.success("Session deleted");
      await refreshTable();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not delete session"
      );
    } finally {
      setDeleteLoadingKey(null);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterDentist !== "all" && r.dentistName !== filterDentist) {
        return false;
      }
      if (filterDay !== "all" && r.day !== filterDay) {
        return false;
      }
      return true;
    });
  }, [rows, filterDentist, filterDay]);

  const openCreate = () => {
    setEditSource(null);
    setIsCreateOpen(true);
  };

  const openEdit = (r: DentistRow) => {
    const doctorId = String(r.doctorId ?? "").trim();
    if (!doctorId) {
      toast.error("Cannot edit: missing dentist id.");
      return;
    }
    setEditSource(r);
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setEditSource(null);
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sm:mx-auto sm:max-w-6xl">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          Dentist General hours
        </h3>
        <button
          type="button"
          onClick={openCreate}
          className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors w-full sm:w-auto"
        >
          Create Session
        </button>
      </div>

      <div className="px-4 pt-3 pb-2 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end sm:gap-4 border-b border-slate-100">
        <label className="space-y-1 block w-full sm:w-auto sm:min-w-[200px]">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Dentist
          </div>
          <select
            value={filterDentist}
            onChange={(e) => setFilterDentist(e.target.value)}
            className="w-full px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All dentists</option>
            {dentistNameOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 block w-full sm:w-auto sm:min-w-[200px]">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Day
          </div>
          <select
            value={filterDay}
            onChange={(e) => setFilterDay(e.target.value)}
            className="w-full px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All days</option>
            {weekDays.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-center">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[160px]">
                Dentist
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[140px]">
                Day
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[200px]">
                Session Name
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Start Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                End Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Room No
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tableLoading ? (
              <tr>
                <td className="px-3 py-6 text-sm text-slate-500" colSpan={7}>
                  Loading…
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-sm text-slate-500" colSpan={7}>
                  No rows.
                </td>
              </tr>
            ) : (
              filteredRows.map((r, idx) => {
                const deleteKey = `${String(r.doctorId ?? "").trim()}__${String(
                  r.roomId ?? ""
                ).trim() || "unassigned"}__${String(r.day ?? "").trim()}__${String(
                  r.sessionName ?? ""
                ).trim()}`;
                return (
                <tr
                  key={`${r.doctorId ?? r.dentistName}-${r.roomId || "unassigned"}-${r.day}-${r.sessionName}-${idx}`}
                  className="hover:bg-slate-50/50"
                >
                  <td className="px-2 py-2 text-sm font-semibold text-slate-800">
                    {r.dentistName}
                  </td>
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.day || "—"}
                  </td>
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.sessionName}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-600">{r.startTime}</td>
                  <td className="px-2 py-2 text-sm text-slate-600">{r.endTime}</td>
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.roomNo ? (
                      r.roomNo
                    ) : (
                      <span className="text-amber-700 font-medium">Unassigned</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="px-2 py-1 rounded text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteRow(r)}
                        disabled={deleteLoadingKey === deleteKey}
                        className="px-2 py-1 rounded text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      >
                        {deleteLoadingKey === deleteKey ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      <AddDentistSessionModal
        open={isCreateOpen}
        onClose={closeCreate}
        onSaved={refreshTable}
        editSource={editSource}
        existingSessions={rows}
      />
    </div>
  );
}
